"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { company } from "@/content/company";
import { usePrefersReducedMotion } from "@/lib/motion";
import { usePrivacyNoticeVisible } from "@/lib/usePrivacyNoticeVisible";
import { WhatsAppIcon } from "@/components/ui/SocialIcons";

// Botão flutuante de WhatsApp, sempre visível por cima do site — anéis
// pulsando em lima (mesma linguagem visual do menu e do device do SAT Labs
// Cast), mas com o glifo real do WhatsApp, não um ícone genérico parecido.

export function WhatsAppFloat() {
  const reduced = usePrefersReducedMotion();
  const noticeVisible = usePrivacyNoticeVisible();
  const [hovered, setHovered] = useState(false);
  // Estado explícito em vez de depender só de initial/animate+delay do
  // framer-motion: nesse componente (montado direto no layout raiz, sem
  // gate condicional como o IntroLoader) o delay isolado não disparava a
  // transição de forma confiável após a hidratação.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setReady(true), 1200);
    return () => clearTimeout(id);
  }, []);

  return (
    <motion.div
      // O aviso de cookies (PrivacyNotice) ocupa quase a largura toda no
      // mobile e fica embaixo — sem esse desvio, ele cobre o botão por
      // completo enquanto estiver na tela.
      // z-45 (entre o header z-40 e o menu mobile z-50): precisa ficar
      // acima do header ao rolar a página, mas atrás do menu mobile quando
      // ele abre — com z-70 o botão flutuava por cima do próprio menu,
      // sobrepondo o link "WhatsApp direto" no rodapé do painel.
      className={`fixed right-6 z-[45] flex items-center gap-3 transition-[bottom] duration-300 ${
        noticeVisible ? "bottom-[200px] sm:bottom-6" : "bottom-6"
      }`}
      initial={false}
      animate={ready ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 16, scale: 0.9 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <AnimatePresence>
        {hovered && (
          <motion.span
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 12 }}
            transition={{ duration: 0.2 }}
            className="hidden whitespace-nowrap rounded-full border border-line bg-bg-raised px-4 py-2 font-mono text-xs uppercase tracking-[0.06em] text-text-primary shadow-lg shadow-black/30 sm:block"
          >
            Fale com a gente no WhatsApp
          </motion.span>
        )}
      </AnimatePresence>

      <a
        href={company.whatsapp.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Falar no WhatsApp"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-accent-lime text-ink-black shadow-[0_0_30px_rgba(153,255,0,0.4)] transition-transform duration-200 hover:scale-105"
      >
        {!reduced && (
          <>
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full border border-accent-lime"
              animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
            />
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full border border-accent-lime"
              animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut", delay: 1.1 }}
            />
          </>
        )}
        <WhatsAppIcon className="relative h-6 w-6" />
      </a>
    </motion.div>
  );
}
